import { GIFTPASS_VALUE_PHP } from "@bednbody/logic";
import type { Tables } from "@bednbody/types";

// GiftPass orders admin (G7, G8; spec docs/specs/giftpass-admin.md). Pure
// helpers only: labels, filters, the print steps and the print-sheet maths.

export type GiftpassOrder = Tables<"giftpass_orders">;
export type GiftpassPackage = Tables<"giftpass_packages">;
export type OrderStatus = GiftpassOrder["status"];
export type PrintStatus = "to_print" | "printed" | "delivered";

export const STATUS_LABEL: Record<string, string> = {
  awaiting_transfer: "Awaiting transfer",
  pending_payment: "Waiting for QR",
  paid: "Paid",
  expired: "Expired",
  failed: "Failed",
  cancelled: "Cancelled",
};

/** Text colour per status: a coloured word, like the executive logs (theme tokens). */
export const STATUS_TONE: Record<string, string> = {
  awaiting_transfer: "text-warning",
  pending_payment: "text-info",
  paid: "text-success",
  expired: "text-muted-foreground",
  failed: "text-muted-foreground",
  cancelled: "text-muted-foreground",
};

export const METHOD_LABEL: Record<string, string> = {
  qrph: "QR Ph",
  bank_transfer: "Bank transfer",
  cash: "Cash",
  ewallet: "E-wallet",
  other: "Other",
};

export const PRINT_STEPS: readonly PrintStatus[] = ["to_print", "printed", "delivered"];

export const PRINT_LABEL: Record<PrintStatus, string> = {
  to_print: "To print",
  printed: "Printed",
  delivered: "Delivered",
};

export type OrderFilter = "all" | "awaiting_transfer" | "paid" | "pending_payment" | "closed";

export const ORDER_FILTERS: readonly { id: OrderFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "awaiting_transfer", label: "Awaiting transfer" },
  { id: "paid", label: "Paid" },
  { id: "pending_payment", label: "Waiting for QR" },
  { id: "closed", label: "Expired or cancelled" },
];

export function matchesFilter(order: Pick<GiftpassOrder, "status">, filter: OrderFilter): boolean {
  if (filter === "all") return true;
  if (filter === "closed") return ["expired", "failed", "cancelled"].includes(order.status);
  return order.status === filter;
}

/** How it was paid. An online order that is paid was paid by QR Ph (0370). */
export function paidBy(
  order: Pick<GiftpassOrder, "status" | "source" | "payment_method">,
): string | null {
  if (order.payment_method) return METHOD_LABEL[order.payment_method] ?? order.payment_method;
  if (order.status === "paid" && order.source === "online") return "QR Ph";
  return null;
}

export function cardsLabel(order: Pick<GiftpassOrder, "physical" | "print_status">): string {
  if (!order.physical) return "Digital only";
  if (!order.print_status) return "After payment";
  return PRINT_LABEL[order.print_status as PrintStatus] ?? order.print_status;
}

/** The step a print-queue button moves to, or null at either end. */
export function printStep(current: string | null, direction: 1 | -1): PrintStatus | null {
  const index = PRINT_STEPS.indexOf(current as PrintStatus);
  if (index < 0) return null;
  return PRINT_STEPS[index + direction] ?? null;
}

/** Only a manual order waiting for its transfer is marked paid or cancelled by hand (0373). */
export function isHandPayable(order: Pick<GiftpassOrder, "source" | "status">): boolean {
  return order.source === "manual" && order.status === "awaiting_transfer";
}

// ── Packages ───────────────────────────────────────────────────────────────

/**
 * What the buyer saves against paying ₱299 a pass, computed — never copied
 * from the old site (spec, Rules). Whole percent; 0 when there is no saving.
 */
export function packageSavings(price: number, passes: number, freePasses: number) {
  const total = passes + freePasses;
  const full = total * GIFTPASS_VALUE_PHP;
  const percent = full > 0 ? Math.max(0, Math.round((1 - price / full) * 100)) : 0;
  const perPass = total > 0 ? price / total : 0;
  return { total, percent, perPass };
}

// ── Print sheets ───────────────────────────────────────────────────────────

/**
 * The card template is 5 × 2.57 in (2026 AUG GP DESIGN): two across and
 * three down on an A4 landscape page.
 */
export const CARDS_PER_PAGE = 6;

/** One cover sheet per order, then its cards, six to a page. */
export function printPages(cardCounts: readonly number[]): number {
  return cardCounts.reduce((sum, cards) => sum + 1 + Math.ceil(cards / CARDS_PER_PAGE), 0);
}

export function peso(amount: number): string {
  return `₱${amount.toLocaleString("en-PH", { maximumFractionDigits: 2 })}`;
}
